/**
 * Alert Settings Dialog Component
 * Dialog for adding/editing alert configurations
 */

import { Component, inject, signal, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormsModule,
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { SelectModule } from 'primeng/select';
import { PaginatedSelectComponent } from '@shared/components/paginated-select/paginated-select.component';
import { createStandardPaginatedLoader } from '@shared/utils/paginated-field.helpers';
import { map } from 'rxjs/operators';
import { TooltipModule } from 'primeng/tooltip';
import { MessageModule } from 'primeng/message';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { AppButtonComponent } from '@app/shared/components/app-button/app-button.component';
import { AlertConfig } from '../../alert-settings.component';
import { BASE_PATH } from '@app/api/stock-service/variables';
import { AlertSettingsService } from '../../services/alert-settings.service';
import { SkuOptionsService } from '@shared/services/sku-options.service';
import { WarehouseOptionsService } from '@shared/services/warehouse-options.service';
import { ErrorTranslatorService } from '@app/shared/services/error-translator-service';
import { extractErrorMessage } from '@app/shared/utils/error-handler.util';

interface ScopeOption {
  label: string;
  value: 'GLOBAL' | 'WAREHOUSE' | 'SKU_WAREHOUSE';
  description: string;
}

const scopeOptions = (ts: TranslateService): ScopeOption[] => [
  {
    label: ts.instant('alert_settings.form.options.scope.global.label'),
    value: 'GLOBAL',
    description: ts.instant(
      'alert_settings.form.options.scope.global.description',
    ),
  },
  {
    label: ts.instant('alert_settings.form.options.scope.warehouse.label'),
    value: 'WAREHOUSE',
    description: ts.instant(
      'alert_settings.form.options.scope.warehouse.description',
    ),
  },
  {
    label: ts.instant('alert_settings.form.options.scope.sku_warehouse.label'),
    value: 'SKU_WAREHOUSE',
    description: ts.instant(
      'alert_settings.form.options.scope.sku_warehouse.description',
    ),
  },
];

@Component({
  selector: 'app-alert-settings-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    InputNumberModule,
    InputTextModule,
    TextareaModule,
    SelectModule,
    PaginatedSelectComponent,
    TooltipModule,
    MessageModule,
    AppButtonComponent,
  ],
  providers: [{ provide: BASE_PATH, useFactory: () => inject(BASE_PATH) }],
  templateUrl: './alert-settings-dialog.component.html',
  styleUrl: './alert-settings-dialog.component.css',
})
export class AlertSettingsDialogComponent implements OnInit {
  private readonly dialogRef = inject(DynamicDialogRef);
  private readonly config = inject(DynamicDialogConfig);
  private readonly translateService = inject(TranslateService);
  private readonly fb = inject(FormBuilder);
  private readonly alertSettingsService = inject(AlertSettingsService);
  private readonly skuOptionsService = inject(SkuOptionsService);
  private readonly warehouseOptionsService = inject(WarehouseOptionsService);
  private readonly errorTranslatorService = inject(ErrorTranslatorService);
  private readonly messageService = inject(MessageService);

  showErrors = signal(false);
  isEditMode = false;
  saving = signal(false);
  errorMessage = signal<string | null>(null);
  scopeOptions: ScopeOption[] = scopeOptions(this.translateService);

  readonly skuPaginatedConfig = createStandardPaginatedLoader((params) =>
      this.skuOptionsService.getOptionsPage(params, { valueKey: 'code' }).pipe(
        map((page) => ({
          options: page.options.map((opt) => ({
            label: opt.label,
            value: String(opt.value),
          })),
          pageContentLength: page.pageContentLength,
          totalElements: page.totalElements,
        })),
      ),
    );

  readonly warehousePaginatedConfig = createStandardPaginatedLoader((params) =>
      this.warehouseOptionsService
        .getOptionsPage(params, { status: true, valueKey: 'code' })
        .pipe(
          map((page) => ({
            options: page.options.map((opt) => ({
              label: opt.label,
              value: String(opt.value),
            })),
            pageContentLength: page.pageContentLength,
            totalElements: page.totalElements,
          })),
        ),
    );

  alertForm: FormGroup = this.fb.group({
    skuCode: ['', []],
    warehouseCode: ['', []],
    scope: ['', [Validators.required]],
    name: [''],
    description: [''],
    minStock: [null, [Validators.min(0)]],
    maxStock: [null, [Validators.min(1)]],
  });

  ngOnInit(): void {
    // When scope changes, update other form controls accordingly
    this.alertForm.get('scope')?.valueChanges.subscribe((scope: string) => {
      this.updateFormControlsByScope(scope);
      this.errorMessage.set(null);
    });

    // Clear error message on any form value changes
    this.alertForm.valueChanges.subscribe(() => this.errorMessage.set(null));

    if (this.config.data?.config) {
      this.isEditMode = true;
      this.setEditModeValues();
      this.updateFormControlsByScope(this.config.data.config.scope);
    }

    // Add custom validator: maxStock must be greater than minStock (only if both are provided)
    this.alertForm.addValidators((form) => {
      const minStock = form.get('minStock')?.value;
      const maxStock = form.get('maxStock')?.value;
      if (
        minStock !== null &&
        minStock !== undefined &&
        maxStock !== null &&
        maxStock !== undefined &&
        maxStock <= minStock
      ) {
        return { maxStockInvalid: true };
      }
      return null;
    });
  }

  private setEditModeValues(): void {
    const config: AlertConfig = this.config.data.config;

    this.alertForm.setValue({
      scope: config.scope,
      skuCode: config.skuCode ?? '',
      warehouseCode: config.warehouseCode ?? '',
      name: config.name || '',
      description: config.description || '',
      minStock: config.minStock ?? null,
      maxStock: config.maxStock ?? null,
    });

    // Use setTimeout to ensure value is set before disabling (for p-select to render correctly)
    setTimeout(() => {
      this.alertForm.get('scope')?.disable();
      this.alertForm.get('skuCode')?.disable();
      this.alertForm.get('warehouseCode')?.disable();
    }, 0);
  }

  private updateFormControlsByScope(scope: string | undefined): void {
    if (!scope) return;

    const skuControl = this.alertForm.get('skuCode');
    const warehouseControl = this.alertForm.get('warehouseCode');

    skuControl?.clearValidators();
    warehouseControl?.clearValidators();

    switch (scope) {
      case 'GLOBAL': {
        // If selected Scope=GLOBAL, clear and disable SKU and Warehouse option
        skuControl?.setValue('');
        skuControl?.disable();
        warehouseControl?.setValue('');
        warehouseControl?.disable();
        break;
      }
      case 'WAREHOUSE': {
        // If selected Scope=WAREHOUSE, clear and disable SKU option, re-enable Warehouse option
        skuControl?.setValue('');
        skuControl?.disable();
        warehouseControl?.setValidators([Validators.required]);
        warehouseControl?.enable();
        break;
      }
      case 'SKU_WAREHOUSE': {
        // If selected Scope=SKU_WAREHOUSE, re-enable SKU and Warehouse options
        skuControl?.setValidators([Validators.required]);
        warehouseControl?.setValidators([Validators.required]);
        skuControl?.enable();
        warehouseControl?.enable();
        break;
      }
    }

    skuControl?.updateValueAndValidity({ emitEvent: false });
    warehouseControl?.updateValueAndValidity({ emitEvent: false });
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onSave(): void {
    if (this.alertForm.invalid) {
      this.showErrors.set(true);
      this.alertForm.markAllAsTouched();
      return;
    }

    if (this.saving()) {
      return; // Prevent multiple submissions
    }

    this.errorMessage.set(null);
    this.saving.set(true);

    const formValue = this.alertForm.getRawValue();
    const config: AlertConfig = {
      scope: formValue.scope,
      skuCode:
        formValue.scope in ['GLOBAL', 'WAREHOUSE']
          ? undefined
          : formValue.skuCode,
      warehouseCode:
        formValue.scope === 'GLOBAL' ? undefined : formValue.warehouseCode,
      name: formValue.name || undefined,
      description: formValue.description || undefined,
      minStock: formValue.minStock || undefined,
      maxStock: formValue.maxStock || undefined,
    };

    if (this.isEditMode && this.config.data?.config?.id) {
      this.alertSettingsService
        .updateAlertConfig(this.config.data.config.id, config)
        .subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: this.translateService.instant('common.messages.success'),
              detail: this.translateService.instant(
                'alert_settings.messages.update_success',
              ),
            });
            // Only close dialog on success
            this.dialogRef.close(true);
          },
          error: (error: any) => {
            this.saving.set(false);
            const errorMessage = extractErrorMessage(error);
            const translated = this.errorTranslatorService.translate(
              errorMessage,
              'alert_settings.messages.save_error',
            );
            this.errorMessage.set(translated);
            // Don't close dialog on error
          },
        });
    } else {
      this.alertSettingsService.createAlertConfig(config).subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('common.messages.success'),
            detail: this.translateService.instant(
              'alert_settings.messages.create_success',
            ),
          });
          // Only close dialog on success
          this.dialogRef.close(true);
        },
        error: (error: any) => {
          this.saving.set(false);
          const errorMessage = extractErrorMessage(error);
          const translated = this.errorTranslatorService.translate(
            errorMessage,
            'alert_settings.messages.save_error',
          );
          this.errorMessage.set(translated);
          // Don't close dialog on error
        },
      });
    }
  }
}
